import { useState } from 'react';
import { 
  CreditCard, 
  CheckCircle2, 
  ArrowUpRight, 
  Calendar, 
  FileText, 
  Sparkles,
  Zap,
  Lock,
  RotateCcw
} from 'lucide-react';
import confetti from 'canvas-confetti';
import { SurchargeCalculatorCard } from './SurchargeCalculatorCard';
import { BillInvoice, TariffPlan, UserAccount } from '../types/energy';
import { InvoiceModal } from './InvoiceModal';
import { dispatchPushNotification } from '../services/notificationService';
import { Language, translations } from '../i18n/translations';

interface BillingViewProps {
  account: UserAccount;
  invoices: BillInvoice[];
  tariff: TariffPlan;
  onUpdateInvoices: (invoices: BillInvoice[]) => void;
  onRequireBiometrics: (onPassed: () => void) => void;
  lang?: Language;
}

export function BillingView({
  account,
  invoices,
  tariff,
  onUpdateInvoices,
  onRequireBiometrics,
  lang = 'ja'
}: BillingViewProps) {
  const [selectedInvoice, setSelectedInvoice] = useState<BillInvoice | null>(null);
  const [filter, setFilter] = useState<'all' | 'due' | 'paid'>('all');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paySuccessModal, setPaySuccessModal] = useState<string | null>(null);

  const t = translations[lang];

  // Find current unpaid bill
  const currentDueInvoice = invoices.find((inv) => inv.status === 'due');

  const filteredInvoices = invoices.filter((inv) => {
    if (filter === 'due') return inv.status === 'due';
    if (filter === 'paid') return inv.status === 'paid';
    return true;
  });

  const handlePayNow = (inv: BillInvoice) => {
    onRequireBiometrics(() => {
      // Biometrics succeeded, perform payment
      setIsProcessingPayment(true);
      setTimeout(() => {
        setIsProcessingPayment(false);
        const updated = invoices.map((item) => {
          if (item.id === inv.id) {
            return {
              ...item,
              status: 'paid' as const,
              amountPaid: item.totalAmount
            };
          }
          return item;
        });
        onUpdateInvoices(updated);
        setPaySuccessModal(inv.invoiceNumber);

        // Dispatch notification
        dispatchPushNotification(
          lang === 'ja' ? 'お支払い完了のお知らせ' : 'Payment Received',
          lang === 'ja'
            ? `請求書 ${inv.invoiceNumber} の電気料金 ¥${inv.totalAmount.toLocaleString()} の決済が完了しました。`
            : `Payment of ¥${inv.totalAmount.toLocaleString()} for ${inv.invoiceNumber} processed successfully.`,
          'bill_paid',
          inv.totalAmount
        );

        // Fire festive confetti
        try {
          confetti({
            particleCount: 80,
            spread: 70,
            origin: { y: 0.6 }
          });
        } catch {
          // ignore
        }
      }, 900);
    });
  };

  return (
    <div className="space-y-5 pb-8">
      {/* Current Balance Due Card */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-teal-950/40 border border-slate-800 p-5 shadow-xl">
        <div className="absolute top-0 right-0 w-44 h-44 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-teal-500/15 border border-teal-500/30 text-teal-400 flex items-center justify-center">
              <CreditCard className="w-4 h-4" />
            </div>
            <div>
              <span className="text-xs font-semibold text-slate-300">{t.accountBalance}</span>
              <p className="text-[11px] text-slate-500">
                {t.autoPayStatus.replace(
                  '{method}',
                  account.paymentDetails || (lang === 'ja' ? '登録済決済方法' : 'Registered Payment Method')
                )}
              </p>
            </div>
          </div>
          <span className="text-xs font-mono text-slate-400">
            {account.accountNumber}
          </span>
        </div>

        {currentDueInvoice ? (
          <div>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="text-3xl font-extrabold text-white tracking-tight tabular-nums font-mono">
                ¥{currentDueInvoice.totalAmount.toLocaleString()}
              </span>
              <span className="text-xs font-bold text-amber-400 bg-amber-950/60 border border-amber-800/40 rounded-lg px-2 py-0.5">
                {t.dueDate.replace('{date}', currentDueInvoice.dueDate)}
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              {t.billingPeriodLabel.replace('{start}', currentDueInvoice.periodStart).replace('{end}', currentDueInvoice.periodEnd)}
            </p>

            {/* Quick 1-Tap Pay Action Button */}
            <div className="mt-4 flex flex-col sm:flex-row gap-2.5">
              <button
                type="button"
                disabled={isProcessingPayment}
                onClick={() => handlePayNow(currentDueInvoice)}
                className="flex-1 h-12 bg-teal-500 hover:bg-teal-400 active:scale-[0.98] text-slate-950 font-bold text-sm rounded-2xl flex items-center justify-center gap-2 transition-transform shadow-lg shadow-teal-500/20 disabled:opacity-50"
              >
                {isProcessingPayment ? (
                  <>
                    <RotateCcw className="w-4 h-4 animate-spin" />
                    <span>{t.processingPayment}</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 text-slate-900" />
                    <span>{t.biometricPayBtn.replace('{amount}', currentDueInvoice.totalAmount.toLocaleString())}</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => setSelectedInvoice(currentDueInvoice)}
                className="h-12 px-4 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-2xl flex items-center justify-center gap-1.5 transition-colors border border-slate-700/60"
              >
                <FileText className="w-4 h-4 text-slate-400" />
                {t.viewPdfBtn}
              </button>
            </div>
          </div>
        ) : (
          <div className="py-3">
            <div className="flex items-center gap-2 text-emerald-400">
              <CheckCircle2 className="w-5 h-5" />
              <span className="text-base font-bold">{t.allBillsPaidTitle}</span>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              {t.allBillsPaidSubtitle
                .replace('{amount}', account.estimatedNextBill.toLocaleString())
                .replace('{date}', account.nextBillingDate === 'ー (契約終了)' ? (lang === 'ja' ? 'ー (契約終了)' : '— (Contract Ended)') : account.nextBillingDate)}
            </p>
          </div>
        )}
      </div>

      {/* Billing Cycle Highlights Card */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl">
        <h4 className="text-sm font-bold text-white mb-3">{t.billingCycleInfoTitle}</h4>
        
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="bg-slate-950/60 border border-slate-800/80 rounded-2xl p-3">
            <div className="flex items-center gap-1.5 text-slate-400 mb-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>{t.cycleFrequencyTitle}</span>
            </div>
            <p className="text-sm font-semibold text-white">{t.cycleFrequencyValue}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {t.nextReadDate.replace('{date}', account.nextBillingDate === 'ー (契約終了)' ? (lang === 'ja' ? 'ー (契約終了)' : '— (Contract Ended)') : account.nextBillingDate)}
            </p>
          </div>

          <div className="bg-slate-950/60 border border-slate-800/80 rounded-2xl p-3">
            <div className="flex items-center gap-1.5 text-slate-400 mb-1">
              <Zap className="w-3.5 h-3.5 text-teal-400" />
              <span>{t.planTypeTitle}</span>
            </div>
            <p className="text-sm font-semibold text-white truncate">
              {lang === 'ja' ? tariff.name : 'SymEnergy 【Night Lifestyle Fit Plan】'}
            </p>
            <p className="text-[11px] text-teal-400 mt-0.5 font-mono">
              {t.dayNightRatePreview
                .replace('{day}', tariff.unitRateDay.toFixed(1))
                .replace('{life}', (tariff.unitRateLife || 32.65).toFixed(1))
                .replace('{night}', tariff.unitRateNight.toFixed(1))}
            </p>
          </div>
        </div>
      </div>

      {/* SymEnergy Power Procurement Surcharge Calculator */}
      <SurchargeCalculatorCard defaultKwh={currentDueInvoice?.kwhUsed || 171} lang={lang} />

      {/* Invoice Ledger & Statements History */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-sm font-bold text-white">{t.statementHistoryTitle}</h4>
            <p className="text-xs text-slate-400">{t.scrapedFromPortalSubtitle}</p>
          </div>

          {/* Filter segment */}
          <div className="flex items-center p-1 bg-slate-950/60 rounded-xl border border-slate-800/80">
            <button
              type="button"
              onClick={() => setFilter('all')}
              className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition-colors ${
                filter === 'all' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {t.filterAll}
            </button>
            <button
              type="button"
              onClick={() => setFilter('due')}
              className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition-colors ${
                filter === 'due' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {t.filterDue}
            </button>
            <button
              type="button"
              onClick={() => setFilter('paid')}
              className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition-colors ${
                filter === 'paid' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {t.filterPaid}
            </button>
          </div>
        </div>

        {/* List of Statements */}
        <div className="divide-y divide-slate-800/60">
          {filteredInvoices.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-xs">
              <FileText className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <p className="font-semibold text-slate-300">
                {lang === 'ja' ? '表示するデータが存在しません' : 'No statements available for this contract location'}
              </p>
              <p className="text-[11px] text-slate-500 mt-1 max-w-md mx-auto leading-relaxed">
                {lang === 'ja' 
                  ? '確定した請求書・検針票はありません。マイページから最新データを同期してください。'
                  : 'No statements found. Sync from SymEnergy MyPage to view statements.'}
              </p>
            </div>
          ) : (
            filteredInvoices.map((inv) => (
              <div
                key={inv.id}
                onClick={() => setSelectedInvoice(inv)}
                className="py-3.5 flex items-center justify-between group cursor-pointer hover:bg-slate-800/30 px-2 rounded-xl transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-2xl flex items-center justify-center ${
                      inv.status === 'paid'
                        ? 'bg-emerald-950/60 text-emerald-400 border border-emerald-800/40'
                        : 'bg-rose-950/60 text-rose-400 border border-rose-800/40'
                    }`}
                  >
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-white group-hover:text-teal-400 transition-colors font-mono">
                        {inv.invoiceNumber}
                      </span>
                      <span
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          inv.status === 'paid'
                            ? 'bg-emerald-950 text-emerald-400 border border-emerald-800/50'
                            : 'bg-rose-950 text-rose-400 border border-rose-800/50'
                        }`}
                      >
                        {inv.status === 'paid' ? t.statusPaidBadge : t.statusDueBadge}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-0.5 font-mono">
                      {inv.periodStart} 〜 {inv.periodEnd} · {inv.kwhUsed} kWh
                    </p>
                  </div>
                </div>

                <div className="text-right flex items-center gap-2">
                  <div>
                    <p className="text-sm font-bold text-white font-mono tabular-nums">
                      ¥{inv.totalAmount.toLocaleString()}
                    </p>
                    <p className="text-[10px] text-slate-500">
                      {inv.status === 'paid' ? t.paidOn.replace('{date}', inv.issueDate) : t.dueOn.replace('{date}', inv.dueDate)}
                    </p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-slate-500 group-hover:text-teal-400 transition-colors" />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Invoice PDF Statement Modal */}
      <InvoiceModal
        invoice={selectedInvoice}
        account={account}
        tariff={tariff}
        onClose={() => setSelectedInvoice(null)}
        onPayNow={handlePayNow}
        lang={lang}
      />

      {/* Payment Success Confirmation Popup */}
      {paySuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fadeIn">
          <div className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-3xl p-6 text-center shadow-2xl">
            <div className="w-14 h-14 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto mb-3">
              <Sparkles className="w-8 h-8 animate-spin" />
            </div>
            <h3 className="text-lg font-bold text-white">{t.paymentSuccessTitle}</h3>
            <p className="text-xs text-slate-400 mt-1">
              {t.paymentSuccessSubtitle.replace('{inv}', paySuccessModal).replace('{holder}', account.accountHolder)}
            </p>
            <button
              type="button"
              onClick={() => setPaySuccessModal(null)}
              className="mt-5 w-full h-11 bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-xs rounded-xl transition-colors"
            >
              {t.closeBtn}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
